import React from "react";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { Shuffle, Undo, SendHorizontal } from "lucide-react";
import {
  isGuessCorrect,
  isGuessRepeated,
  shuffleGameData,
} from "../../lib/game-helpers";
import {
  trackGuessSubmitted,
  trackRepeatedGuess,
  trackShuffle,
  trackDeselect,
} from "../../lib/analytics";

import { GameStatusContext } from "../../providers/GameStatusProvider";
import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";

function GameControlButtonsPanel({
  flatWordsList,
  setFlatWordsList,
  setGridShake,
}) {
  const {
    isGameOver,
    guessCandidate,
    setGuessCandidate,
    submittedGuesses,
    setSubmittedGuesses,
    solvedGameData,
    setSolvedGameData,
  } = React.useContext(GameStatusContext);
  const { gameData, categorySize } = React.useContext(PuzzleDataContext);
  const { toast } = useToast();

  function deselectAll() {
    trackDeselect();
    setGuessCandidate([]);
  }

  function submitCandidateGuess() {
    // check that its a valid guess by size
    if (guessCandidate.length !== categorySize) {
      return;
    }
    // check that the guess hasnt already been submitted previously
    if (isGuessRepeated({ submittedGuesses, guessCandidate })) {
      trackRepeatedGuess();
      toast({
        label: "Notification",
        title: "Repeated Guess",
        description: "You previously made this guess!",
      });

      return;
    }
    // add guess to state
    setSubmittedGuesses([...submittedGuesses, guessCandidate]);
    // check if the guess is correct
    const {
      isCorrect,
      correctWords,
      correctCategory,
      isGuessOneAway,
      correctDifficulty,
      correctImageSrc,
    } = isGuessCorrect({
      guessCandidate,
      gameData,
    });

    // Track the guess submission
    trackGuessSubmitted(isCorrect, isGuessOneAway, submittedGuesses.length + 1);

    // if the guess is correct:
    // set it as solved in game data
    if (isCorrect) {
      setSolvedGameData([
        ...solvedGameData,
        {
          category: correctCategory,
          words: correctWords,
          difficulty: correctDifficulty,
          imageSrc: correctImageSrc,
        },
      ]);
      setGuessCandidate([]);
    } else {
      // Shake the grid to give feedback that they were wrong
      setGridShake(true);
      if (isGuessOneAway) {
        toast({
          label: "Notification",
          title: "Close Guess",
          description:
            "You were one guess away from correctly guessing a category!",
        });
      }
    }
  }

  return (
    <div className="w-full flex gap-2">
      <Button
        disabled={isGameOver}
        variant="outline"
        onClick={() => {
          trackShuffle();
          // Shuffle the flat list
          const shuffled = [...flatWordsList].sort(() => Math.random() - 0.5);
          setFlatWordsList(shuffled);
        }}
        className="flex-grow border-[#B8A17F]/40 text-[#4A5D3F] hover:bg-[#B8A17F]/10 hover:border-[#B8A17F] backdrop-blur-sm bg-white/40 font-[525] transition-all duration-300"
      >
        <Shuffle className="w-4 h-4 mr-2" />
        Shuffle
      </Button>
      <Button
        disabled={isGameOver || guessCandidate.length === 0}
        variant="outline"
        onClick={deselectAll}
        className="flex-grow border-[#B8A17F]/40 text-[#4A5D3F] hover:bg-[#B8A17F]/10 hover:border-[#B8A17F] backdrop-blur-sm bg-white/40 font-[525] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300"
      >
        Deselect All
      </Button>
      <Button
        onClick={submitCandidateGuess}
        disabled={isGameOver || guessCandidate.length !== categorySize}
        className="flex-grow bg-gradient-to-br from-[#6B7F5E] to-[#4A5D3F] hover:from-[#4A5D3F] hover:to-[#2C3A28] text-white font-[525] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300"
      >
        Submit
      </Button>
    </div>
  );
}

export default GameControlButtonsPanel;
